import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Loader2, Heart } from "lucide-react";
import { api } from "@/lib/api";

export default function FriendSummary() {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [basedOn, setBasedOn] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/friend-summary");
      setMessage(data.message);
      setBasedOn(data.based_on);
    } catch {
      setMessage("I'm having a quiet moment — try refreshing in a bit ♡");
    }
  };

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-[2rem] overflow-hidden border border-kawaii-border p-6 md:p-8 mb-8"
      style={{ background: "linear-gradient(120deg, #FFF9F9 0%, #FFE5E5 55%, #E2F0CB 100%)" }}
      data-testid="friend-summary-card"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-kawaii-pink/25 blur-3xl" />
      <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-kawaii-lavender/25 blur-3xl" />

      <div className="relative flex items-start gap-4">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-kawaii-pink to-kawaii-peach flex items-center justify-center shadow-sm flex-shrink-0 animate-float">
          <Heart className="w-5 h-5 text-white" strokeWidth={2.5} fill="white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold">A note from Lymphie</span>
            {basedOn > 0 && (
              <span className="text-[10px] text-kawaii-textSoft/70 font-body">· based on your last {basedOn} {basedOn === 1 ? 'entry' : 'entries'}</span>
            )}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-kawaii-textSoft text-sm font-body">
              <Loader2 className="w-4 h-4 animate-spin" /> catching up with your week...
            </div>
          ) : (
            <p className="font-heading text-lg md:text-xl text-kawaii-text leading-snug" data-testid="friend-summary-text">
              {message}
            </p>
          )}
        </div>
        <button
          onClick={refresh}
          disabled={refreshing || loading}
          data-testid="refresh-friend-summary"
          title="New reflection"
          className="kawaii-btn w-9 h-9 rounded-full bg-white/70 backdrop-blur border border-white/60 flex items-center justify-center flex-shrink-0 disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin text-kawaii-textSoft" /> : <RefreshCw className="w-4 h-4 text-kawaii-textSoft" strokeWidth={2.5} />}
        </button>
      </div>
    </motion.div>
  );
}
